import { DocsService } from '@affine/core/modules/doc';
import { useLiveData, useService } from '@toeverything/infra';
import { useEffect, useMemo, useState } from 'react';

import type { NotesIndexEntry } from '../handlers/add-to-notes';
import { NOTES_CHANGED_EVENT } from '../types';

export type { NotesIndexEntry } from '../handlers/add-to-notes';

export interface NotesIndex {
  /** Id of the Notes doc, or null if none has been created yet. */
  notesDocId: string | null;
  /** All entries, newest-first. */
  entries: ReadonlyArray<NotesIndexEntry>;
  /** Entries grouped by source doc, each list newest-first. */
  entriesByDoc: ReadonlyMap<string, ReadonlyArray<NotesIndexEntry>>;
  /** Convenience: entries clipped from a single source doc. */
  getEntries: (sourceDocId: string) => ReadonlyArray<NotesIndexEntry>;
}

const EMPTY: NotesIndex = {
  notesDocId: null,
  entries: [],
  entriesByDoc: new Map(),
  getEntries: () => [],
};

/**
 * Reactive notes index for the active workspace.
 *
 * Source of truth is `custom:notesIndex` on the Notes doc — a
 * JSON-encoded array of `NotesIndexEntry`. We scan
 * `DocsService.allDocProperties$` (the same workspace-wide stream
 * `useBranchGraph` uses) and pick the doc that carries the index.
 * Updates flow through `allDocProperties$` automatically; the
 * `NOTES_CHANGED_EVENT` listener forces an extra refresh on
 * save/undo so the inline panel never lags behind the toast.
 */
export function useNotesIndex(): NotesIndex {
  const docsService = useService(DocsService);
  const allProps = useLiveData(docsService.allDocProperties$);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const onChange = () => setTick(t => t + 1);
    window.addEventListener(NOTES_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(NOTES_CHANGED_EVENT, onChange);
  }, []);

  return useMemo<NotesIndex>(() => {
    void tick;
    if (!allProps) return EMPTY;
    let notesDocId: string | null = null;
    let entries: NotesIndexEntry[] = [];
    for (const [docId, props] of Object.entries(allProps)) {
      const raw = (props as Record<string, unknown>)['custom:notesIndex'];
      if (typeof raw !== 'string') continue;
      try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) continue;
        notesDocId = docId;
        entries = parsed as NotesIndexEntry[];
        break; // first index wins; there's only one Notes doc per workspace
      } catch {
        continue;
      }
    }
    const byDoc = new Map<string, NotesIndexEntry[]>();
    for (const entry of entries) {
      if (!entry.sourceDocId) continue;
      const list = byDoc.get(entry.sourceDocId) ?? [];
      list.push(entry);
      byDoc.set(entry.sourceDocId, list);
    }
    for (const list of byDoc.values()) {
      list.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    }
    return {
      notesDocId,
      entries,
      entriesByDoc: byDoc,
      getEntries: (sourceDocId: string) => byDoc.get(sourceDocId) ?? [],
    };
  }, [allProps, tick]);
}
